/* eslint-disable jira/integration/test-id-by-folder-structure */
/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useRef, useState, type FormEvent } from 'react';
import debounce from 'lodash/debounce';
import Field from '@atlaskit/form/field';
import { ErrorMessage } from '@atlaskit/form/error-message';
import { HelperMessage } from '@atlaskit/form/helper-message';
import { useFormState } from '@atlaskit/form/use-form-state';
import Textfield from '@atlaskit/textfield/text-field';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { VALIDATION_ERROR_EMPTY, BOARD_NAME_LIMIT } from './boardTypes.constants.tsx';
import type { BoardCreateFormValues } from './BoardCreateFormValues.types.tsx';

const boardNameFieldLabel = defineMessage({
	id: 'software-board-create.modal.form.board-name.board-name-field-label',
	defaultMessage: 'Name this board',
	description: 'Label for text field asking user to enter a board name',
});

const boardNameFieldPlaceholder = defineMessage({
	id: 'software-board-create.modal.form.board-name.board-name-field-placeholder',
	defaultMessage: 'Enter a name for this board',
	description: 'Placeholder for text field asking user to enter a board name',
});

const boardNameEmptyError = defineMessage({
	id: 'software-board-create.modal.form.board-name.board-name-empty-error',
	defaultMessage: 'Enter a board name',
	description: 'Error message for board name field telling the user that board name is required',
});

const boardNameMaxChars = defineMessage({
	id: 'software-board-create.modal.form.board-name.board-name-max-chars',
	defaultMessage: 'Maximum {maxLength, plural, one {# character} other {# characters}}',
	description:
		'A hint below the board name input to tell the user the maximum number of characters allowed for the board name',
});

export const BoardNameField = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const formState = useFormState<BoardCreateFormValues>();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [value, setValue] = useState('');
	const hasEdited = useRef(false);

	const debouncedHandleOnChange = useRef(
		// oxlint-disable-next-line react/react-compiler
		debounce(() => {
			if (!hasEdited.current) {
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'changed',
						actionSubject: 'input',
					}),
					'boardName',
				);
				hasEdited.current = true;
			}
		}, 300),
	).current;

	const handleOnChange = useCallback(
		(event: FormEvent<HTMLInputElement>) => {
			setValue(event.currentTarget.value);
			const newValue = event.currentTarget.value;
			setValue(newValue);
			debouncedHandleOnChange();
		},
		[debouncedHandleOnChange],
	);

	return (
		<Field<BoardCreateFormValues['boardName']>
			label={formatMessage(boardNameFieldLabel)}
			name="boardName"
			isRequired
		>
			{({ fieldProps: { onChange, ...rest }, error }) => (
				<>
					<Textfield
						{...rest}
						placeholder={formatMessage(boardNameFieldPlaceholder)}
						maxLength={BOARD_NAME_LIMIT}
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						testId="software-board-create.ui.modal.form.board-name.board-name-input"
						value={value}
						onChange={(event) => {
							handleOnChange(event);
							onChange(event);
						}}
					/>
					{error === VALIDATION_ERROR_EMPTY && (
						// eslint-disable-next-line jira/integration/test-id-by-folder-structure
						<ErrorMessage testId="software-board-create.ui.modal.form.board-name.board-name-error">
							{formatMessage(boardNameEmptyError)}
						</ErrorMessage>
					)}
					{formState?.values.boardName && formState.values.boardName.length >= BOARD_NAME_LIMIT && (
						<HelperMessage>
							{formatMessage(boardNameMaxChars, {
								maxLength: BOARD_NAME_LIMIT,
							})}
						</HelperMessage>
					)}
				</>
			)}
		</Field>
	);
};
